import { backendPublicPath, getServiceUrlOrigin, joinServiceUrl } from "@/api/servicePaths";
import { API_URL } from "@/configs";
import { getPreferredAuthLocale } from "@/lib/auth/preferredLocale";
import { TtlPromiseCache } from "@/lib/cache/ttlPromiseCache";

import FETCH from "../fetch";
import FETCHRAW from "../fetchRaw";

// Use Session.attemptRefreshingSession via import Session from "supertokens-auth-react/recipe/session";
// export const getRefreshToken = (sRefreshToken: string, spTenantId: string) => {
//   if (sRefreshToken && spTenantId) {
//     const headers = {
//       "st-auth-mode": "header",
//       rid: "session",
//       "sp-tenant-id": spTenantId
//     };
//     headers["Authorization"] = sRefreshToken;
//     // headers["st-auth-mode"] = "cookie";
//     return FETCHRAW({
//       type: "apiurl",
//       path: "/auth/session/refresh/",
//       method: "POST",
//       headers: headers,
//       withToken: false,
//       body: JSON.stringify({})
//     });
//   }
// };

const convertBody = (body) => {
  const newBody = {
    formFields: Object.keys(body).map((obj) => {
      return {
        id: obj,
        value: body[obj]
      };
    })
  };
  return JSON.stringify(newBody);
};

const backendRootUrl = (path: string): string => joinServiceUrl(getServiceUrlOrigin(API_URL) || API_URL, path);

const authLocaleHeaders = () => ({
  "Accept-Language": getPreferredAuthLocale()
});

export const loginApi = (
  body: { email: string; password: string; "recaptcha-token"?: string },
  header: { "sp-tenant-id": string; "st-auth-mode": string }
) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/auth/signin/"),
    method: "post",
    headers: header,
    body: convertBody(body),
    withToken: false
  });

export const resolvePasswordResetPin = (body: { email: string; pin: string }) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/stokens/password-reset/resolve-pin/"),
    method: "post",
    body: JSON.stringify(body),
    withToken: false
  });

export const signinup = (
  body: {
    thirdPartyId: string;
    // clientType: string;
    "invitation-code"?: string;
    "sp-tenant-id": string;
    shouldTryLinkingWithSessionUser?: boolean;
    redirectURIInfo: {
      redirectURIOnProviderDashboard: string;
      redirectURIQueryParams: {
        code: string;
        state: string;
      };
      // pkceCodeVerifier: "abcd1234";
    };
  },
  header: { "sp-tenant-id": string },
  options?: { withToken?: boolean }
) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/auth/signinup/"),
    method: "post",
    headers: header,
    body: JSON.stringify(body),
    withToken: options?.withToken ?? false
  });

// api get info user by email
export const getUser = (
  serverSideReq?: any,
  params?: { limit?: string; filter?: string; order_by?: string; offset?: string }
) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/system/users/"),
    params: params,
    method: "GET",
    serverSideReq
    // params: params ,  TODO: move to /setting
  });

// api get user info for table
export const getUserTable = ({
  params,
  serverSideReq
}: {
  params: {
    [x: string]: string;
  };
  serverSideReq?: any;
}) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/system/users/"),
    params: params,
    method: "GET",
    serverSideReq
    // TODO: move to /setting
  });

interface ApiResponse<T> {
  content: T;
  // Add other fields that might be in the response
  // status?: number;
  // message?: string;
}

export interface ActiveSessionRecord {
  sessionHandle: string;
  userId?: string;
  isCurrentSession: boolean;
  createdAt?: string | null;
  expiresAt?: string | null;
  lastSeenAt?: string | null;
  userAgent?: string | null;
  deviceSummary?: string | null;
  clientIp?: string | null;
  region?: string | null;
  locationSummary?: string | null;
}

export interface ActiveSessionsResponse {
  currentSessionHandle: string;
  sessions: ActiveSessionRecord[];
}

export interface SecurityReviewEventRecord {
  id: string;
  eventType: string;
  occurredAt?: string | null;
  ipAddress?: string | null;
  region?: string | null;
  deviceSummary?: string | null;
  emailStatus?: string | null;
  tenantName?: string | null;
}

export interface SecurityReviewEventsResponse {
  events: SecurityReviewEventRecord[];
  code: number;
}

const userProfileCache = new TtlPromiseCache<any>(120_000);

export interface UserProfileRequestOptions {
  suppressAuthLogoutOn401?: boolean;
  signal?: AbortSignal;
}

export const clearUserProfileCache = (id: string | number) => {
  userProfileCache.delete(String(id));
};

export const clearAllUserProfileCache = (): void => {
  userProfileCache.clear();
};

export const refreshUserById = async <T = any,>(
  id: string | number,
  serverSideReq?: any,
  options?: UserProfileRequestOptions
): Promise<ApiResponse<T>> => {
  if (typeof window === "undefined" || serverSideReq) {
    return FETCHRAW({
      type: "apiurl",
      path: backendPublicPath(`/user_profile/${id}/`),
      method: "GET",
      serverSideReq,
      suppressAuthLogoutOn401: options?.suppressAuthLogoutOn401,
      signal: options?.signal
    }) as Promise<ApiResponse<T>>;
  }

  const cacheKey = String(id);
  return userProfileCache.getFresh(cacheKey, () => {
    return FETCHRAW({
      type: "apiurl",
      path: backendPublicPath(`/user_profile/${id}/`),
      method: "GET",
      serverSideReq,
      suppressAuthLogoutOn401: options?.suppressAuthLogoutOn401,
      signal: options?.signal
    }) as Promise<ApiResponse<T>>;
  }) as Promise<ApiResponse<T>>;
};

// api get info user by id
export const getUserById = async <T = any,>(
  id: string | number,
  serverSideReq?: any,
  options?: UserProfileRequestOptions
): Promise<ApiResponse<T>> => {
  if (typeof window === "undefined" || serverSideReq) {
    return FETCHRAW({
      type: "apiurl",
      path: backendPublicPath(`/user_profile/${id}/`),
      method: "GET",
      serverSideReq,
      suppressAuthLogoutOn401: options?.suppressAuthLogoutOn401,
      signal: options?.signal
    }) as Promise<ApiResponse<T>>;
  }

  const cacheKey = String(id);

  return userProfileCache.get(cacheKey, () => {
    return FETCHRAW({
      type: "apiurl",
      path: backendPublicPath(`/user_profile/${id}/`),
      method: "GET",
      serverSideReq,
      suppressAuthLogoutOn401: options?.suppressAuthLogoutOn401,
      signal: options?.signal
    }) as Promise<ApiResponse<T>>;
  }) as Promise<ApiResponse<T>>;
};

export const getTenantVerifiedCallerIds = async <T = any,>(serverSideReq?: any): Promise<ApiResponse<T>> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/user_profile/twilio/verified-caller-ids/"),
    method: "GET",
    serverSideReq
  }) as Promise<ApiResponse<T>>;
};

export const deleteTenantVerifiedCallerId = async (id: string): Promise<ApiResponse<any>> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/user_profile/twilio/verified-caller-ids/${id}/`),
    method: "DELETE"
  }) as Promise<ApiResponse<any>>;
};

//api update info user
export const updateUser = (body: any, id: number | string) => {
  clearUserProfileCache(id);
  //TODO: any -> User from @/models/user.ts, but it's not fit with the current User model
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/user_profile/${id}/`),
    method: "PUT",
    body: JSON.stringify(body)
  });
};

//api get user's integrations auth url
export const getServiceAuthUrl = (service: string, services?: string[]) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/oauth/${service}/login/`),
    method: "GET",
    params: services ? { services: services.join(",") } : undefined
  });

export const updateAgentPermissions = (provider: string, agent_permissions: any) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/integrations/agent-permissions/${provider}/`),
    method: "POST",
    body: JSON.stringify({ agent_permissions })
  });

export const disconnectProviderAuth = (provider) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/oauth/${provider}/logout/`),
    method: "POST"
  });

export const requestAccountDeletion = async (): Promise<ApiResponse<any>> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/user_profile/"),
    method: "DELETE"
  }) as Promise<ApiResponse<any>>;
};

export const getActiveSessions = async <T = ActiveSessionsResponse,>(serverSideReq?: any): Promise<ApiResponse<T>> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/user_profile/sessions/"),
    method: "GET",
    serverSideReq
  }) as Promise<ApiResponse<T>>;
};

export const revokeActiveSession = async (sessionHandle: string): Promise<ApiResponse<any>> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/user_profile/sessions/${sessionHandle}/`),
    method: "DELETE"
  }) as Promise<ApiResponse<any>>;
};

export const revokeOtherActiveSessions = async (): Promise<ApiResponse<any>> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/user_profile/sessions/revoke-others/"),
    method: "POST"
  }) as Promise<ApiResponse<any>>;
};

export const getSecurityReviewEvents = async <T = SecurityReviewEventsResponse,>(): Promise<ApiResponse<T>> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/user_profile/security-events/"),
    method: "GET"
  }) as Promise<ApiResponse<T>>;
};

export const getGmailAuthUrl = () =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/oauth/gmail/login/"),
    method: "GET"
  });

export const requestGmailAuthUrl = (body) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/oauth/gmail/login/"),
    method: "POST",
    body: JSON.stringify(body)
  });

export const getOutlookAuthUrl = () =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/oauth/outlook/login/"),
    method: "GET"
  });

export const requestOutlookAuthUrl = (body) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/oauth/outlook/login/"),
    method: "POST",
    body: JSON.stringify(body)
  });

export const getSlackInstallUrl = (tenantGuid?: string) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/slackapp/install-url/"),
    method: "GET",
    params: tenantGuid ? { tenant_guid: tenantGuid } : undefined
  });

// api register new user
export const registerOrg = (body: { email: string; name: string; password: string }) =>
  FETCH("mock", "/users", "post", {}, body);

export const register = (body: {
  email: string;
  password: string;
  "sp-tenant-id": string;
  "invitation-code": string;
  name?: string;
  timezone: string;
}) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/auth/signup/"),
    method: "post",
    headers: authLocaleHeaders(),
    body: convertBody(body),
    withToken: false
  });

export const registerSandboxUser = (body: {
  email: string;
  password: string;
  "sp-tenant-id": string;
  "invitation-code": string;
  name: string;
}) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/auth/signup/"),
    method: "post",
    headers: authLocaleHeaders(),
    body: convertBody(body),
    withToken: false
  });

export const requestAccess = (body: {
  full_name: string;
  email: string;
  // password: string;
  reason_for_access: string;
}) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/request-access/"),
    method: "post",
    body: JSON.stringify(body),
    withToken: false
  });

export const getCsrfNew = () =>
  FETCHRAW({ type: "apiurl", path: backendRootUrl("/"), method: "GET", withToken: false });

export const generateInvitationCode = (body: {
  code: string;
  usage_limit?: number;
  expire_after?: Date;
  description?: string;
  tenant_id?: string;
}) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/invitation-code/create-code/"),
    method: "post",
    body: JSON.stringify(body)
  });

export const sendOtp = (body: { email?: string; phoneNumber?: string }, header?: Record<string, string>) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/auth/signinup/code/"),
    method: "POST",
    headers: {
      ...authLocaleHeaders(),
      ...(header || {})
    },
    body: JSON.stringify(body)
  });

export const resendOtp = (body: { deviceId: string; preAuthSessionId: string }, header?: Record<string, string>) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/auth/signinup/code/resend/"),
    method: "POST",
    headers: {
      ...authLocaleHeaders(),
      ...(header || {})
    },
    body: JSON.stringify(body)
  });

export const verifyOtp = (
  body: { preAuthSessionId: string; deviceId: string; userInputCode: string },
  header?: Record<string, string>
) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/auth/signinup/code/consume/"),
    method: "POST",
    headers: header || {},
    body: JSON.stringify(body)
  });

export const enable2FA = (body: { enable: boolean; method: string }) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/stokens/mfa/enable/"),
    method: "POST",
    body: JSON.stringify(body)
  });

export const get2FAStatus = () =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/stokens/mfa/enable/"),
    method: "GET"
  });

// New: fetch user list for selection/search (safe fields only)
export const fetchUserListData = (
  params?: { limit?: string; filter?: string; order_by?: string; offset?: string },
  serverSideReq?: any
) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/system/user-list/"),
    params: params,
    method: "GET",
    serverSideReq
  });
export const changePasswordApi = (body: { old_password: string; new_password: string }) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/users/change-password/"),
    method: "POST",
    body: JSON.stringify(body)
  });

export const adminReset2FA = (userGuid: string) =>
  FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/users/totp/admin-reset/"),
    method: "POST",
    body: JSON.stringify({ user_guid: userGuid })
  });
